// BOARD-RESULTS WP6 (docs/design/2026-11-02-board-stages-gates-kpis.md §5): a Research card's
// signal as card fields, so the Marketing Director and the board filter and sort findings by
// numbers instead of reading card text. This file is the ONE place that maps the Researcher's
// output (research-watch `digest-format.md`: the `trends` card object and the `## Trends` line)
// to the fields; the skill reference (docs/agents/htva/skills/researcher/finding-fields.md) is
// checked against `findingFieldTable()` by a test, and the server's setup, the findings read and
// the card's evidence block all use these definitions.
import type { FieldOptions, FieldType } from "./fields";

export const SIGNALS = ["urgent", "trending", "rising", "evergreen"] as const;
export type Signal = (typeof SIGNALS)[number];
export const TREND_DIRECTIONS = ["up", "flat", "down", "new"] as const;
export type TrendDirection = (typeof TREND_DIRECTIONS)[number];

/** The statuses a finding is filed into and parked to (content pipeline keys, §1). */
export const FINDING_STATUS = { research: "research", parked: "parked" } as const;

export const SIGNAL_FIELD = {
  signal: "signal",
  trendDirection: "trend_direction",
  searchInterest: "search_interest",
  engagement: "engagement",
  sources: "sources",
  evidence: "evidence",
  whyItMatters: "why_it_matters",
  offerFit: "offer_fit",
  expires: "expires",
} as const;
export type SignalFieldKey = (typeof SIGNAL_FIELD)[keyof typeof SIGNAL_FIELD];

const titleCase = (k: string) => k.charAt(0).toUpperCase() + k.slice(1);
const choiceList = (keys: readonly string[]) => keys.map((key) => ({ key, name: titleCase(key) }));

/** A field the setup creates (by name, so a board that already has it keeps its own). */
export type SignalFieldDef = {
  key: SignalFieldKey;
  name: string;
  type: FieldType;
  options?: FieldOptions;
  /** Required on entering Research (`field_rule.required_on: enter`, WP1). */
  requiredInResearch: boolean;
  /** What the field holds, as the skill reference says it. */
  meaning: string;
  /** Where the Researcher's output feeds it: the `trends` card key, or how it is derived. */
  from: string;
};

/**
 * Choices in rank order: a view sorted by Signal ascending lists urgent first (selects sort by
 * choice position, view-query.ts `sortKeys`).
 */
export const RESEARCH_SIGNAL_FIELDS: readonly SignalFieldDef[] = [
  {
    key: SIGNAL_FIELD.signal,
    name: "Signal",
    type: "select",
    options: { choices: choiceList(SIGNALS) },
    requiredInResearch: true,
    meaning: "urgent (act this week) · trending · rising · evergreen",
    from: "status",
  },
  {
    key: SIGNAL_FIELD.trendDirection,
    name: "Trend direction",
    type: "select",
    options: { choices: choiceList(TREND_DIRECTIONS) },
    requiredInResearch: false,
    meaning: "up · flat · down · new, from Google Trends or the source's own numbers",
    from: "direction",
  },
  {
    key: SIGNAL_FIELD.searchInterest,
    name: "Search interest",
    type: "number",
    requiredInResearch: false,
    meaning: "the Google Trends index, or the searches a day the source shows",
    from: "signal: the number before searches / search / interest / index",
  },
  {
    key: SIGNAL_FIELD.engagement,
    name: "Engagement",
    type: "number",
    requiredInResearch: false,
    meaning: "the best example's likes + comments + shares (or views)",
    from: "signal: the numbers before likes / comments / shares / saves / views / reactions, summed",
  },
  {
    key: SIGNAL_FIELD.sources,
    name: "Sources",
    type: "number",
    options: { precision: 0 },
    requiredInResearch: true,
    meaning: "distinct sources seen (1 or more)",
    from: "sources",
  },
  {
    key: SIGNAL_FIELD.evidence,
    name: "Evidence",
    type: "text",
    requiredInResearch: true,
    meaning: "the numbers line, then one line per source link",
    from: "signal, then links (one per line)",
  },
  {
    key: SIGNAL_FIELD.whyItMatters,
    name: "Why it matters",
    type: "text",
    requiredInResearch: true,
    meaning: "the audience pain point in plain words (280 characters at most)",
    from: "pain",
  },
  {
    key: SIGNAL_FIELD.offerFit,
    name: "Offer fit",
    type: "text",
    requiredInResearch: false,
    meaning: "one line linking the finding to our offer",
    from: "offer",
  },
  {
    key: SIGNAL_FIELD.expires,
    name: "Expires",
    type: "date",
    requiredInResearch: false,
    meaning: "when an urgent finding stops mattering; the day after, the card moves to Parked",
    from: "set by the Researcher for urgent findings (not in the digest)",
  },
];

export const WHY_IT_MATTERS_CAP = 280;

/** The Researcher's `trends` card object (research-watch digest-format.md), as it files it. */
export type TrendFinding = {
  topic: string;
  status: string;
  direction?: string | undefined;
  /** "the numbers: 50k+ searches today | 3 new suggestions since <date> | 340 comments" */
  signal?: string | undefined;
  sources?: number | undefined;
  links?: readonly string[] | undefined;
  pain?: string | undefined;
  offer?: string | undefined;
  /** ISO date; the Researcher sets it on urgent findings. */
  expires?: string | undefined;
};

/** "50k+" → 50000, "1.2m" → 1200000, "340" → 340. */
export function parseCount(s: string): number | null {
  const m = /^(\d+(?:[.,]\d+)?)\s*([km])?\+?$/i.exec(s.trim());
  if (!m) return null;
  const n = Number((m[1] as string).replace(",", "."));
  const mult = m[2]?.toLowerCase() === "k" ? 1e3 : m[2]?.toLowerCase() === "m" ? 1e6 : 1;
  return Number.isFinite(n) ? Math.round(n * mult) : null;
}

const NUMBER_BEFORE = (words: string) =>
  new RegExp(`(\\d+(?:[.,]\\d+)?\\s*[km]?\\+?)\\s+(?:[a-z-]+\\s+)?(?:${words})\\b`, "gi");
const ENGAGEMENT_WORDS = "likes?|comments?|shares?|saves?|views?|reactions?|upvotes?|replies";
const INTEREST_WORDS = "searches|search|interest|index";

/** The numbers of a `signal` line: engagement summed, search interest the largest. */
export function signalNumbers(signal: string): {
  engagement: number | null;
  searchInterest: number | null;
} {
  const all = (re: RegExp) =>
    [...signal.matchAll(re)]
      .map((m) => parseCount(m[1] as string))
      .filter((n): n is number => n !== null);
  const eng = all(NUMBER_BEFORE(ENGAGEMENT_WORDS));
  const interest = all(NUMBER_BEFORE(INTEREST_WORDS));
  return {
    engagement: eng.length ? eng.reduce((a, b) => a + b, 0) : null,
    searchInterest: interest.length ? Math.max(...interest) : null,
  };
}

const oneOf = <T extends string>(list: readonly T[], v: string | undefined): T | null => {
  const k = (v ?? "").trim().toLowerCase() as T;
  return list.includes(k) ? k : null;
};

/**
 * The field values (by field key) a finding sets; keys with nothing to set are left out. Choice
 * values are choice keys; Evidence is the numbers line, then one line per link.
 */
export function findingFieldValues(f: TrendFinding): Partial<Record<SignalFieldKey, unknown>> {
  const out: Partial<Record<SignalFieldKey, unknown>> = {};
  const signal = oneOf(SIGNALS, f.status);
  if (signal) out.signal = signal;
  const dir = oneOf(TREND_DIRECTIONS, f.direction);
  if (dir) out.trend_direction = dir;
  if (f.signal) {
    const n = signalNumbers(f.signal);
    if (n.engagement !== null) out.engagement = n.engagement;
    if (n.searchInterest !== null) out.search_interest = n.searchInterest;
  }
  const links = (f.links ?? []).map((l) => l.trim()).filter(Boolean);
  const sources = f.sources ?? (links.length || undefined);
  if (sources !== undefined && sources >= 1) out.sources = Math.floor(sources);
  const evidence = [f.signal?.trim(), ...links].filter(Boolean).join("\n");
  if (evidence) out.evidence = evidence;
  if (f.pain?.trim()) out.why_it_matters = f.pain.trim().slice(0, WHY_IT_MATTERS_CAP);
  if (f.offer?.trim()) out.offer_fit = f.offer.trim();
  if (f.expires && /^\d{4}-\d{2}-\d{2}/.test(f.expires)) out.expires = f.expires.slice(0, 10);
  return out;
}

/**
 * A `## Trends` digest line back into a finding (cards filed before the fields existed carry it
 * in their text):
 * `- [urgent] <topic> | evidence: <direction>, <numbers>, <n> sources: <link>[, <link>] | pain: … | offer: …`
 */
export function parseTrendLine(line: string): TrendFinding | null {
  const m = /^\s*-\s*\[([a-z]+)\]\s*(.+)$/i.exec(line);
  if (!m) return null;
  const [topic, ...parts] = (m[2] as string).split(/\s+\|\s+/);
  const part = (k: string) =>
    parts
      .find((p) => p.toLowerCase().startsWith(`${k}:`))
      ?.slice(k.length + 1)
      .trim();
  const ev = part("evidence") ?? "";
  const srcAt = /(\d+)\s+sources?\s*:\s*(.*)$/i.exec(ev);
  const before = (srcAt ? ev.slice(0, srcAt.index) : ev).replace(/,\s*$/, "").trim();
  const [first, ...rest] = before.split(/,\s*/);
  const direction = oneOf(TREND_DIRECTIONS, first) ?? undefined;
  const numbers = (direction ? rest : [first, ...rest]).filter(Boolean).join(", ");
  return {
    topic: (topic ?? "").trim(),
    status: (m[1] as string).toLowerCase(),
    direction,
    signal: numbers || undefined,
    sources: srcAt ? Number(srcAt[1]) : undefined,
    links: srcAt
      ? (srcAt[2] as string)
          .split(/,\s*/)
          .map((s) => s.trim())
          .filter(Boolean)
      : [],
    pain: part("pain"),
    offer: part("offer"),
  };
}

/** One Evidence line for the card: its text and, when it is (or ends in) a link, the link. */
export function evidenceLines(
  text: string | null | undefined,
): { text: string; url: string | null }[] {
  return (text ?? "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const url = /(https?:\/\/\S+)\s*$/.exec(l)?.[1] ?? null;
      return { text: l, url };
    });
}

export const signalRank = (s: string | null | undefined): number => {
  const i = SIGNALS.indexOf((s ?? "") as Signal);
  return i < 0 ? SIGNALS.length : i;
};

export type FindingSortable = {
  signal: string | null;
  engagement: number | null;
  search_interest: number | null;
  sources: number | null;
};

/** Urgent first, then the stronger evidence: engagement, search interest, sources (desc). */
export function compareFindings(a: FindingSortable, b: FindingSortable): number {
  const n = (x: number | null) => x ?? -1;
  return (
    signalRank(a.signal) - signalRank(b.signal) ||
    n(b.engagement) - n(a.engagement) ||
    n(b.search_interest) - n(a.search_interest) ||
    n(b.sources) - n(a.sources)
  );
}

/** The skill reference's mapping table, generated here so the doc cannot drift from the code. */
export function findingFieldTable(): string {
  const rows = RESEARCH_SIGNAL_FIELDS.map(
    (f) =>
      `| ${f.name} | \`${f.key}\` | ${f.type} | ${f.from} | ${f.meaning} | ${f.requiredInResearch ? "yes" : "no"} |`,
  );
  return [
    "| Field | Key | Type | From the finding | Holds | Required in Research |",
    "|---|---|---|---|---|---|",
    ...rows,
  ].join("\n");
}
